import { addDays, atNoon, daysUntil, displayDate, startOfToday, toDateKey } from '../lib/format';
import type { Client, ComplianceDocument, Job, JobRecord, RodentPlan, StationType, Technician, User } from './types';

export const PEST_SERVICES = [
  { label: 'Cockroaches', code: 'C' },
  { label: 'Ants', code: 'A' },
  { label: 'Spiders', code: 'S' },
  { label: 'Rodents', code: 'R' },
  { label: 'Termite inspection', code: 'V' }
];
export const ADDITIONAL_SERVICES = ['General pest treatment', 'Termite treatment', 'Termite remedial treatment', 'Termite monitoring', 'Bird control'];
export const ACTIVE_PESTS = ['Cockroaches', 'Ants', 'Spiders', 'Rodents', 'Termites', 'Birds'];
export const DURATIONS = [{ value: 30, label: '30 minutes' }, { value: 45, label: '45 minutes' }, { value: 60, label: '1 hour' }, { value: 90, label: '1.5 hours' }, { value: 120, label: '2 hours' }, { value: 180, label: '3 hours' }];
export const CLIENT_TYPES = ['Residential', 'Commercial', 'High risk'];
export const WORK_TYPES = ['Employee', 'Contractor', 'Manager'];
export const COMPETENCIES = ['Urban pests', 'Timber pests', 'Pest birds', 'High-risk places', 'Sensitive places', 'Fumigation', 'CPPUPM3005', 'CPPUPM3006', 'CPPUPM3008', 'CPPUPM3010', 'CPPUPM3011', 'CPPUPM3018'];
export const DOCUMENT_TAGS = ['SDS', 'Label', 'Safety information', 'Other compliance document'];
export const ROLE_LABELS: Record<string, string> = { admin: 'Administrator', operations: 'Operations', tech: 'Technician' };

export const STATION_TYPES: Record<StationType, { label: string; color: string }> = {
  'non-chemical': { label: 'Non-chemical', color: '#438b5d' },
  fgar: { label: 'FGAR', color: '#e1b52f' },
  sgar: { label: 'SGAR', color: '#c8493f' },
  monitoring: { label: 'Monitoring station', color: '#73787a' },
  'tracking-powder': { label: 'Tracking powder', color: '#8150a6' }
};

export const isManager = (user: User | null | undefined) => user?.role === 'admin' || user?.role === 'operations';

export function buildService(services: string[], additional: string) {
  const codes = PEST_SERVICES.filter((service) => services.includes(service.label)).map((service) => service.code).join('');
  return { serviceCode: codes || additional, detail: [...PEST_SERVICES.filter((service) => services.includes(service.label)).map((service) => service.label), additional].filter(Boolean).join(', ') };
}

export type ServiceClass = 'general' | 'rodent' | 'termite';
export function serviceClass(job: Pick<Job, 'services' | 'additional' | 'serviceCode'>): ServiceClass {
  if (job.services.includes('Rodents')) return 'rodent';
  if (job.services.includes('Termite inspection') || job.additional.startsWith('Termite')) return 'termite';
  if (!job.services.length && job.serviceCode.startsWith('Rodent')) return 'rodent';
  return 'general';
}

export const isComplete = (job: Job) => job.completed && !job.cancelled;
export const isOpen = (job: Job) => !job.cancelled && !job.completed;
export const jobTitle = (job: Job) => job.detail || job.serviceCode || 'Pest service';
export const byStartTime = (a: Job, b: Job) => (a.startTime || '').localeCompare(b.startTime || '');

export function clientIsHighRisk(client: Client | undefined | null) {
  return Boolean(client && (client.highRisk || client.type === 'High risk'));
}

export function findClientForJob(job: Pick<Job, 'clientId' | 'clientName'>, clients: Client[]) {
  return clients.find((client) => client.id === job.clientId) || clients.find((client) => client.name === job.clientName) || null;
}

export function jobsForClient(client: Client, jobs: Job[]) {
  return jobs.filter((job) => job.clientId === client.id || (!job.clientId && job.clientName === client.name));
}

export function recordsForClient(client: Client, records: JobRecord[]) {
  return records.filter((record) => record.clientId === client.id || (!record.clientId && record.clientName === client.name));
}

export function plansForClient(client: Client, plans: RodentPlan[]) {
  return plans.filter((plan) => plan.clientId === client.id || (!plan.clientId && plan.clientName === client.name));
}

/** Technician names offered in job and run-sheet pickers: the register plus anyone already on a job. */
export function technicianNames(technicians: Technician[], jobs: Job[]) {
  return [...new Set([...technicians.map((tech) => tech.name), ...jobs.map((job) => job.techName)].filter(Boolean))];
}

/** Whether a job belongs to the signed-in technician (linked technician record first, then name). */
export function isMyJob(job: Job, user: User, technicians: Technician[]) {
  const linked = technicians.find((tech) => tech.id === user.technicianId);
  if (linked && (job.techId === linked.id || job.techName === linked.name)) return true;
  return job.techName === user.displayName;
}

export type Period = '7' | '30' | '90' | 'fy' | 'all' | 'custom';
export function periodRange(value: Period, custom?: { start: string; end: string }) {
  const today = new Date(); today.setHours(12, 0, 0, 0);
  let start = new Date(today); let end = new Date(today);
  if (value === 'fy') start = new Date(today.getMonth() < 6 ? today.getFullYear() - 1 : today.getFullYear(), 6, 1, 12);
  else if (value === 'all') start = new Date(2000, 0, 1, 12);
  else if (value === 'custom') { start = atNoon(custom?.start || '2000-01-01'); end = atNoon(custom?.end || '2099-12-31'); }
  else start = addDays(today, -Number(value) + 1);
  return { start, end };
}
export function jobsInRange(jobs: Job[], range: { start: Date; end: Date }) {
  return jobs.filter((job) => { const date = atNoon(job.date); return date >= range.start && date <= range.end; });
}

// Document review: due within 30 days, or overdue.
export function documentsDueForReview(documents: ComplianceDocument[]) {
  const window = addDays(startOfToday(), 30);
  return documents.filter((doc) => doc.expiry && new Date(`${doc.expiry}T00:00:00`) <= window).sort((a, b) => a.expiry.localeCompare(b.expiry));
}

// SGAR (second-generation anticoagulant) bait sites must be reviewed at least every 35 days.
export const SGAR_REVIEW_DAYS = 35;
export const sgarCount = (plan: RodentPlan) => plan.stations.filter((station) => station.type === 'sgar').length;
export function lastReviewDate(plan: RodentPlan): Date | null {
  const key = plan.lastSiteReviewDate || toDateKey(plan.savedAt);
  return key ? new Date(`${key}T00:00:00`) : null;
}
export function sgarDue(plan: RodentPlan): { days: number | null; due: Date | null } {
  const reviewed = lastReviewDate(plan);
  if (!reviewed) return { days: null, due: null };
  const due = addDays(reviewed, SGAR_REVIEW_DAYS);
  return { days: daysUntil(due), due };
}
export function sgarStatus(plan: RodentPlan): { label: string; state: 'current' | 'due' | 'overdue' } {
  if (!sgarCount(plan)) return { label: 'No SGAR stations', state: 'current' };
  const { days, due } = sgarDue(plan);
  if (days === null || !due) return { label: 'SGAR review date not recorded', state: 'overdue' };
  if (days < 0) return { label: `SGAR review overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`, state: 'overdue' };
  if (days === 0) return { label: 'SGAR review due today', state: 'due' };
  if (days <= 7) return { label: `SGAR review due in ${days} day${days === 1 ? '' : 's'}`, state: 'due' };
  return { label: `SGAR review due ${displayDate(due.toISOString())}`, state: 'current' };
}

/** Latest ARMP for each job, the unit that needs review. */
export function latestArmpByJob(records: JobRecord[]) {
  const map = new Map<string, JobRecord>();
  records.filter((record) => record.type === 'ARMP').forEach((record) => {
    const key = record.jobId || `${record.clientName}|${record.jobDate}|${record.jobTime}|${record.jobService}`;
    const existing = map.get(key);
    if (!existing || existing.createdAt < record.createdAt) map.set(key, record);
  });
  return map;
}
